/* Storefront event owner: native Page View/native product view; custom PDP details and valid buy intent. */
(() => {
  'use strict';
  if (window.UnchemedTracking || document.body.dataset.designMode === 'true') return;
  const pending = [], listStates = new WeakMap(), seenLists = new Set(), formSuccesses = new WeakSet();
  const debug = new URLSearchParams(location.search).get('unc_tracking_debug') === '1';
  const allowedEvents = new Set(['view_content', 'view_item_list', 'add_to_cart', 'lead', 'subscribe', 'sign_up']);
  const verifiedGiftSkus = new Map([['10064787931383:51074671345911','UNC-GIFT-CX-SPA']]);
  let retryTimer = 0, viewQueued = false, listTimer = 0, observer;
  const round = value => Math.round((value + Number.EPSILON) * 100) / 100;
  /* Item ids are Shopify product ids, so Meta must receive content_type product_group (matches the native PDP). */
  const productGroupPage = /(^|\/)pages\/(pdp-v1-79|chelate-x(?:-v[1-3])?|unc-original-shower|pdp-v1)\/?$/.test(location.pathname) ||
    /(^|\/)products\/(unchemed-chelate-x-filtered-handheld-shower|duo-20-complete-kit|chelate-x-bundles|duo-20-bundles)\/?$/.test(location.pathname);
  function consentAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy) return true;
      const analytics = typeof privacy.analyticsProcessingAllowed === 'function' ? privacy.analyticsProcessingAllowed() : null;
      const marketing = typeof privacy.marketingAllowed === 'function' ? privacy.marketingAllowed() : null;
      return !(analytics === false && marketing === false);
    } catch (_) { return true; }
  }
  function note(status, name, data) {
    if (!debug) return;
    console.info('[Unchemed Upstack]', JSON.stringify({status, event:name, ...data}));
  }
  function flush() {
    clearTimeout(retryTimer);
    const now = Date.now();
    for (let i = 0; i < pending.length;) {
      const entry = pending[i];
      if (!consentAllowed() || now > entry.expires) {
        pending.splice(i, 1); entry.resolve(false);
        note('not_handed_to_sdk', entry.name, {reason:consentAllowed() ? 'sdk_unavailable' : 'consent_denied'});
        continue;
      }
      if (typeof window._upstack !== 'function') { i++; continue; }
      try {
        window._upstack('track', entry.name, entry.data);
        pending.splice(i, 1); entry.resolve(true);
        note('handed_to_sdk', entry.name, entry.name === 'lead' || entry.name === 'subscribe' || entry.name === 'sign_up' ? {source:entry.data.source} : entry.data);
      } catch (_) { i++; }
    }
    if (pending.length) retryTimer = setTimeout(flush, 250);
  }
  function track(name, data) {
    if (!allowedEvents.has(name) || !consentAllowed()) return Promise.resolve(false);
    return new Promise(resolve => {
      if (pending.length >= 50) { resolve(false); return; }
      pending.push({name, data, resolve, expires:Date.now() + 30000});
      flush();
    });
  }
  function eventData(product, variantId, includeGift = false, quantity = 1) {
    const variant = product?.variants?.find(v => String(v.id) === String(variantId));
    const currency = String(product?.currency || '').toUpperCase();
    const cents = Number(variant?.price);
    quantity = Number(quantity);
    if (!product?.id || !variant?.id || variant.price == null || !Number.isFinite(cents) || cents < 0 || !Number.isInteger(quantity) || quantity < 1 || !/^[A-Z]{3}$/.test(currency)) return null;
    const variantName = variant.title || variant.name || '';
    const name = product.name + (variantName && variantName !== 'Default Title' ? ' - ' + variantName : '');
    const items = [{id:String(product.id), variant:String(variant.id), product_id:String(product.id), variant_id:String(variant.id), sku:variant.sku || '', name, price:cents / 100, quantity, brand:'Unchemed', type:'product'}];
    if (includeGift && product.gift?.price === 0 && product.gift.id && product.gift.variant) {
      const gift = product.gift;
      const sku = gift.sku || verifiedGiftSkus.get(String(gift.id) + ':' + String(gift.variant)) || '';
      items.push({id:String(gift.id), variant:String(gift.variant), product_id:String(gift.id), variant_id:String(gift.variant), sku, name:gift.name, price:0, quantity:1, brand:'Unchemed', type:'product'});
    }
    if (productGroupPage) items.forEach(item => { item.content_type = 'product_group'; });
    return {items, value:round(cents * quantity / 100), currency};
  }
  function cartAccepted(product, variantId, quantity = 1) {
    const variant = product?.variants?.find(v => String(v.id) === String(variantId));
    if (!variant?.available) return Promise.resolve(false);
    const data = eventData(product, variantId, true, quantity);
    return data ? track('add_to_cart', data) : Promise.resolve(false);
  }
  function formProduct(form) {
    const script = form?.closest('[data-unc-buy],[data-lander]')?.querySelector('[data-coras-product], [data-lander-product]');
    return script ? JSON.parse(script.textContent) : null;
  }
  function view() {
    if (viewQueued || !['pdp', 'listicle'].includes(document.body.dataset.uncPage) || !consentAllowed()) return;
    try {
      const requested = new URLSearchParams(location.search).get('variant');
      const forms = [...document.querySelectorAll('form[data-coras-form]')];
      const form = forms.find(f => requested && f.elements.id && f.elements.id.value === requested) || forms[0];
      const classic = window.UnchemedClassicContext;
      const cx = window.UnchemedCXContext;
      const product = formProduct(form);
      const requestedVariant = product?.variants?.find(item => String(item.id) === requested && item.available);
      const data = cx?.product?.productLandingPage === true
        ? eventData(cx.product, cx.ids[cx.getSelected()])
        : classic?.product?.productLandingPage === true
          ? eventData(classic.product, classic.ids[classic.getSelected()])
          : eventData(product, requestedVariant?.id || form?.elements.id?.value);
      if (!data) return;
      viewQueued = true;
      track('view_content', data).then(accepted => { if (!accepted) viewQueued = false; });
    } catch (_) {}
  }
  /* Native product pages: Upstack's own view_content already reports the product (product_group, selected package value),
     so the theme sends no second view_content there (removed 2026-10-05 to stop the duplicate Meta ViewContent). */
  function visibleItem(node) {
    if (document.hidden || !node.getClientRects().length || node.closest('[hidden], [inert]')) return false;
    const style = getComputedStyle(node);
    if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') return false;
    const rect = node.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth) return false;
    const x = Math.max(0, Math.min(innerWidth - 1, rect.left + rect.width / 2));
    const y = Math.max(0, Math.min(innerHeight - 1, (Math.max(0, rect.top) + Math.min(innerHeight, rect.bottom)) / 2));
    const top = document.elementFromPoint(x, y);
    return !!top && (node.contains(top) || top.contains(node));
  }
  function listViews() {
    clearTimeout(listTimer);
    if (!consentAllowed()) return;
    document.querySelectorAll('[data-upstack-list]').forEach(list => {
      const all = [...list.querySelectorAll('[data-upstack-item]')];
      const fresh = [];
      all.forEach((node, index) => {
        if (listStates.get(node) || !visibleItem(node)) return;
        try {
          const item = JSON.parse(node.getAttribute('data-upstack-item'));
          if (!item.id || !item.variant || !Number.isFinite(item.price)) return;
          const key = list.dataset.upstackList + ':' + item.id + ':' + item.variant;
          if (seenLists.has(key)) { listStates.set(node, true); return; }
          fresh.push({node, key, item:{...item, index}});
        } catch (_) {}
      });
      if (!fresh.length) return;
      fresh.forEach(({node, key}) => {listStates.set(node, true); seenLists.add(key);});
      track('view_item_list', {itemListId:list.dataset.upstackList, itemListName:list.dataset.upstackListName || list.dataset.upstackList, currency:list.dataset.upstackCurrency || window.Shopify?.currency?.active || 'USD', items:fresh.map(entry => entry.item)}).then(accepted => {
        if (!accepted) fresh.forEach(({node, key}) => {listStates.delete(node); seenLists.delete(key);});
      });
    });
  }
  function scheduleLists() { clearTimeout(listTimer); listTimer = setTimeout(listViews, 100); }
  function confirmedForms() {
    document.querySelectorAll('[data-upstack-success]').forEach(node => {
      if (formSuccesses.has(node) || !consentAllowed()) return;
      const name = node.dataset.upstackSuccess;
      if (!['lead', 'subscribe', 'sign_up'].includes(name)) return;
      formSuccesses.add(node);
      track(name, {source:node.dataset.upstackSource || 'storefront_form', ...(name === 'subscribe' ? {channel:node.dataset.upstackChannel || 'email'} : {}), ...(name === 'sign_up' ? {method:node.dataset.upstackMethod || 'email'} : {})}).then(accepted => {if (!accepted) formSuccesses.delete(node);});
    });
  }
  function init() {
    queueMicrotask(view); confirmedForms();
    if ('IntersectionObserver' in window) {
      if (!observer) observer = new IntersectionObserver(scheduleLists, {threshold:[0, 0.5]});
      document.querySelectorAll('[data-upstack-item]').forEach(node => observer.observe(node));
    }
    scheduleLists();
  }
  window.UnchemedTracking = Object.freeze({cartAccepted, cartClicked:cartAccepted, eventData, version:'2026-10-07.bundles-product-group'});

  document.addEventListener('visitorConsentCollected', () => {flush(); init();});
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('toggle', scheduleLists, true);
  document.addEventListener('visibilitychange', () => {if (!document.hidden) init();});
  document.addEventListener('click', scheduleLists, true);
  document.addEventListener('scroll', scheduleLists, {capture:true, passive:true});
  window.addEventListener('resize', scheduleLists, {passive:true});
  window.addEventListener('pageshow', () => {flush(); init();});
  window.addEventListener('pagehide', () => {flush(); clearTimeout(retryTimer); clearTimeout(listTimer);});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();

/* In-page links scroll to their section without changing the URL. A hash or replaceState URL change makes
   Upstack's SPA listener send an extra page_view, so on the landers below we scroll without touching history.
   Acts only on same-page links that no other script already handled (event.defaultPrevented). */
(() => {
  'use strict';
  if (window.UnchemedAnchorScroll || !(/(^|\/)pages\/(pdp-v1-79|chelate-x(?:-v[1-3])?|unc-original-shower|pdp-v1)\/?$/.test(location.pathname) ||
      /(^|\/)products\/(unchemed-chelate-x-filtered-handheld-shower|duo-20-complete-kit)\/?$/.test(location.pathname))) return;
  const root = document.documentElement, stopEvents = ['touchstart', 'pointerdown', 'wheel', 'keydown'];
  const px = value => parseFloat(value) || 0;
  let run = 0;
  const offset = target => px(getComputedStyle(target).scrollMarginTop) + px(getComputedStyle(root).scrollPaddingTop);
  function jump(delta) {
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, Math.max(0, window.scrollY + delta));
    root.style.scrollBehavior = previous;
  }
  function go(target, options = {}) {
    if (!target?.isConnected) return false;
    const id = ++run, started = Date.now();
    let behavior = options.behavior || 'auto';  /* auto = the page's own CSS scroll-behavior, exactly like a native anchor */
    if (behavior === 'smooth' && matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'instant';
    let lastY = NaN, still = 0, fixes = 0, alignedSince = 0;
    const cancel = () => { if (id === run) run++; };
    const cleanup = () => stopEvents.forEach(type => window.removeEventListener(type, cancel, true));
    stopEvents.forEach(type => window.addEventListener(type, cancel, {capture:true, passive:true}));
    target.scrollIntoView({behavior, block:'start'});
    const tick = () => {
      const now = Date.now();
      if (id !== run || now - started > 8000) return cleanup();
      const y = window.scrollY;
      still = Math.abs(y - lastY) < 1 ? still + 1 : 0;
      lastY = y;
      if (still >= 3) {
        const delta = target.getBoundingClientRect().top - offset(target);
        const max = root.scrollHeight - window.innerHeight;
        const blocked = (delta > 0 && y >= max - 1) || (delta < 0 && y <= 0);
        if (Math.abs(delta) > 2 && !blocked && fixes < 6) { fixes++; jump(delta); still = 0; alignedSince = 0; }
        else {
          if (!alignedSince) alignedSince = now;
          if (now - alignedSince >= 1000 && now - started >= 1500) return cleanup();
        }
      }
      setTimeout(tick, 100);
    };
    setTimeout(tick, 100);
    return true;
  }
  window.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href^="#"]');
    const href = link?.getAttribute('href') || '';
    if (href.length < 2 || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(href.slice(1))); } catch (_) { return; }
    if (!target) return;
    event.preventDefault();
    go(target);
    if (event.detail === 0) {
      if (!target.matches('a[href],button,input,select,textarea,summary,[tabindex]')) target.setAttribute('tabindex', '-1');
      target.focus({preventScroll:true});
    }
  });
  window.UnchemedAnchorScroll = Object.freeze({go, version:'2026-10-05.no-hash-2'});
})();
